import * as React from "react";
import * as styles from "./TreeSunlight.css";
import * as foliageStyles from "./TreeSunlightFoliage.css";

type LeafComponent = {
    x: number;
    y: number;
    width: number;
    height: number;
    centerX: number;
    centerY: number;
    area: number;
    pixels: Array<{ x: number; y: number; alpha: number }>;
};

type LeafCluster = {
    sourceWidth: number;
    sourceHeight: number;
    anchorX: number;
    anchorY: number;
    rotation: number;
    phase: number;
    speed: number;
    opacity: number;
    scale: number;
    sprite: HTMLCanvasElement;
};

type CoverMetrics = {
    imageScale: number;
    renderedWidth: number;
    renderedHeight: number;
    offsetX: number;
    offsetY: number;
};

const AnalysisWidth = 280;
const MaxClusters = 86;

export function AnimatedLeafClusters({ imageSrc }: { imageSrc: string }) {
    const layerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const layer = layerRef.current;
        if (!layer) return;

        let disposed = false;
        let imageReady = false;
        let canvasWidth = 1;
        let canvasHeight = 1;
        let clusters: LeafCluster[] = [];
        let clusterAspect = 0;
        const image = new Image();

        const renderSprites = () => {
            if (disposed || !imageReady) return;
            const cover = getCoverMetrics(canvasWidth, canvasHeight, image);
            const fragment = document.createDocumentFragment();
            for (const cluster of clusters) {
                const point = imageUvToCanvasPoint(cluster.anchorX, cluster.anchorY, cover);
                const width = cluster.sourceWidth * cover.imageScale * cluster.scale;
                const height = cluster.sourceHeight * cover.imageScale * cluster.scale;
                const duration = 10 + cluster.speed * 2.8;
                const sprite = cluster.sprite;
                const travel = 10 + Math.min(25, width * 0.7);
                const lift = 3.5 + Math.min(11, height * 0.32);
                sprite.className = foliageStyles.leafSprite;
                sprite.style.left = `${point.x}px`;
                sprite.style.top = `${point.y}px`;
                sprite.style.width = `${width}px`;
                sprite.style.height = `${height}px`;
                sprite.style.setProperty("--leaf-sway-x", `${travel}px`);
                sprite.style.setProperty("--leaf-sway-y", `${lift}px`);
                sprite.style.setProperty("--leaf-duration", `${duration}s`);
                sprite.style.setProperty("--leaf-delay", `${-cluster.phase / (Math.PI * 2) * duration}s`);
                sprite.style.setProperty("--leaf-rotation", `${cluster.rotation}rad`);
                sprite.style.setProperty("--leaf-opacity", `${cluster.opacity}`);
                fragment.appendChild(sprite);
            }
            layer.replaceChildren(fragment);
        };

        const resize = () => {
            const bounds = layer.getBoundingClientRect();
            canvasWidth = Math.max(1, bounds.width);
            canvasHeight = Math.max(1, bounds.height);
            if (imageReady && Math.abs(clusterAspect - canvasWidth / canvasHeight) > 0.18) {
                clusters = createLeafClusters(image, canvasWidth, canvasHeight);
                clusterAspect = canvasWidth / canvasHeight;
                renderSprites();
            }
        };

        image.decoding = "async";
        image.onload = () => {
            if (disposed) return;
            imageReady = true;
            resize();
        };
        image.src = imageSrc;
        window.addEventListener("resize", resize);
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
        observer?.observe(layer);

        return () => {
            disposed = true;
            window.removeEventListener("resize", resize);
            observer?.disconnect();
            image.onload = null;
            layer.replaceChildren();
        };
    }, [imageSrc]);

    return <div ref={layerRef} className={styles.leafClusterCanvas} aria-hidden="true" />;
}

function getCoverMetrics(width: number, height: number, image: HTMLImageElement): CoverMetrics {
    const imageScale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const renderedWidth = image.naturalWidth * imageScale;
    const renderedHeight = image.naturalHeight * imageScale;
    const canvasAspect = width / height;
    const imageAspect = image.naturalWidth / image.naturalHeight;
    const visibleHeight = imageAspect / canvasAspect;
    const visibleWidth = canvasAspect / imageAspect;
    return {
        imageScale,
        renderedWidth,
        renderedHeight,
        offsetX: canvasAspect > imageAspect ? 0 : -(1 - visibleWidth) * 0.5 * renderedWidth,
        offsetY: canvasAspect > imageAspect ? -(1 - visibleHeight) * 0.12 * renderedHeight : 0,
    };
}

function imageUvToCanvasPoint(anchorX: number, anchorY: number, cover: CoverMetrics) {
    return { x: cover.offsetX + anchorX * cover.renderedWidth, y: cover.offsetY + anchorY * cover.renderedHeight };
}

function createLeafClusters(image: HTMLImageElement, outputWidth: number, outputHeight: number): LeafCluster[] {
    const width = AnalysisWidth;
    const height = Math.max(1, Math.round(width * image.naturalHeight / image.naturalWidth));
    const imageAspect = image.naturalWidth / image.naturalHeight;
    const outputAspect = outputWidth / outputHeight;
    const visibleHeight = outputAspect > imageAspect ? imageAspect / outputAspect : 1;
    const visibleWidth = outputAspect > imageAspect ? 1 : outputAspect / imageAspect;
    const visibleTop = outputAspect > imageAspect ? (1 - visibleHeight) * 0.12 : 0;
    const visibleLeft = outputAspect > imageAspect ? 0 : (1 - visibleWidth) * 0.5;
    const visibleBottom = visibleTop + visibleHeight;
    const visibleRight = visibleLeft + visibleWidth;
    const analysisCanvas = document.createElement("canvas");
    analysisCanvas.width = width;
    analysisCanvas.height = height;
    const analysisContext = analysisCanvas.getContext("2d", { willReadFrequently: true });
    if (!analysisContext) return [];
    analysisContext.drawImage(image, 0, 0, width, height);
    const components = findLeafComponents(analysisContext.getImageData(0, 0, width, height).data, width, height)
        .filter((component) => component.area >= 10 && component.area <= 1_400)
        .filter((component) => component.width >= 3 && component.height >= 2 && component.width <= 58 && component.height <= 58)
        .filter((component) => {
            const u = component.centerX / width;
            const v = component.centerY / height;
            return (
                u >= visibleLeft - 0.025 &&
                u <= visibleRight + 0.025 &&
                v >= visibleTop - 0.025 &&
                v <= visibleBottom + 0.025 &&
                !(Math.abs(u - 0.53) < 0.13 && Math.abs(v - 0.31) < 0.1)
            );
        });
    const random = (seed: number) => {
        const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
        return value - Math.floor(value);
    };
    components.sort((a, b) => random(a.x * 17 + a.y * 31) - random(b.x * 17 + b.y * 31));
    const clusterCount = Math.min(MaxClusters, outputWidth < 700 ? 72 : 86);
    return components.slice(0, clusterCount).map((component, index) => {
        const padding = 2 + random(index * 5 + 301) * 2;
        const analysisCropX = Math.max(0, component.x - padding);
        const analysisCropY = Math.max(0, component.y - padding);
        const analysisCropWidth = Math.min(width - analysisCropX, component.width + padding * 2);
        const analysisCropHeight = Math.min(height - analysisCropY, component.height + padding * 2);
        const sourceX = analysisCropX / width * image.naturalWidth;
        const sourceY = analysisCropY / height * image.naturalHeight;
        const sourceWidth = Math.min(
            image.naturalWidth - sourceX,
            analysisCropWidth / width * image.naturalWidth,
        );
        const sourceHeight = Math.min(
            image.naturalHeight - sourceY,
            analysisCropHeight / height * image.naturalHeight,
        );
        const sprite = document.createElement("canvas");
        sprite.width = Math.max(1, Math.ceil(sourceWidth));
        sprite.height = Math.max(1, Math.ceil(sourceHeight));
        const spriteContext = sprite.getContext("2d");
        if (spriteContext) {
            spriteContext.imageSmoothingEnabled = true;
            spriteContext.drawImage(
                image,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                0,
                0,
                sprite.width,
                sprite.height,
            );
            const mask = document.createElement("canvas");
            mask.width = Math.max(1, Math.ceil(analysisCropWidth));
            mask.height = Math.max(1, Math.ceil(analysisCropHeight));
            const maskContext = mask.getContext("2d");
            if (maskContext) {
                const maskData = maskContext.createImageData(mask.width, mask.height);
                for (const pixel of component.pixels) {
                    const maskX = Math.round(pixel.x - analysisCropX);
                    const maskY = Math.round(pixel.y - analysisCropY);
                    if (maskX < 0 || maskY < 0 || maskX >= mask.width || maskY >= mask.height) continue;
                    const maskIndex = (maskY * mask.width + maskX) * 4;
                    maskData.data[maskIndex] = 255;
                    maskData.data[maskIndex + 1] = 255;
                    maskData.data[maskIndex + 2] = 255;
                    maskData.data[maskIndex + 3] = Math.min(255, pixel.alpha * 1.7);
                }
                maskContext.putImageData(maskData, 0, 0);
                spriteContext.globalCompositeOperation = "destination-in";
                spriteContext.drawImage(mask, 0, 0, mask.width, mask.height, 0, 0, sprite.width, sprite.height);
                spriteContext.globalCompositeOperation = "source-over";
            }
        }
        return {
            sourceWidth,
            sourceHeight,
            anchorX: component.centerX / width,
            anchorY: component.centerY / height,
            rotation: (random(index * 7 + 313) - 0.5) * 0.4,
            phase: random(index * 11 + 317) * Math.PI * 2,
            speed: 0.7 + random(index * 13 + 331) * 1.1,
            opacity: 0.94 + random(index * 17 + 337) * 0.06,
            scale: 1.15 + random(index * 19 + 347) * 0.35,
            sprite,
        };
    });
}

function findLeafComponents(pixels: Uint8ClampedArray, width: number, height: number): LeafComponent[] {
    const threshold = 90;
    const seen = new Uint8Array(width * height);
    const components: LeafComponent[] = [];
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const start = y * width + x;
            if (seen[start] || pixels[start * 4 + 3] < threshold) continue;
            const queue = [start];
            seen[start] = 1;
            let area = 0;
            let minX = x;
            let maxX = x;
            let minY = y;
            let maxY = y;
            let sumX = 0;
            let sumY = 0;
            const componentPixels: Array<{ x: number; y: number; alpha: number }> = [];
            for (let index = 0; index < queue.length; index += 1) {
                const position = queue[index];
                const pointX = position % width;
                const pointY = Math.floor(position / width);
                const alpha = pixels[position * 4 + 3];
                area += 1;
                sumX += pointX;
                sumY += pointY;
                componentPixels.push({ x: pointX, y: pointY, alpha });
                minX = Math.min(minX, pointX);
                maxX = Math.max(maxX, pointX);
                minY = Math.min(minY, pointY);
                maxY = Math.max(maxY, pointY);
                for (const [offsetX, offsetY] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                    const nextX = pointX + offsetX;
                    const nextY = pointY + offsetY;
                    if (nextX < 0 || nextY < 0 || nextX >= width || nextY >= height) continue;
                    const next = nextY * width + nextX;
                    if (!seen[next] && pixels[next * 4 + 3] >= threshold) {
                        seen[next] = 1;
                        queue.push(next);
                    }
                }
            }
            components.push({
                x: minX,
                y: minY,
                width: maxX - minX + 1,
                height: maxY - minY + 1,
                centerX: sumX / area,
                centerY: sumY / area,
                area,
                pixels: componentPixels,
            });
        }
    }
    return components;
}
